/**
 * The words the acknowledgment screens agree on — §7.7.
 *
 * Two of them carry the design, and both are easy to render in a way that
 * quietly contradicts it.
 *
 * **The audience is a question, not a list.** §7.7 resolves membership
 * dynamically — by role, or by holding a capability on a space — precisely so a
 * new hire enrols into a cycle opened before they existed. So
 * {@link AudienceDescription} renders the *rule* ("everyone with the staff
 * role"), never a roster, and says out loud that the set is re-read on every
 * load. A screen that showed "11 people" as though it were a membership would be
 * the snapshot the spec spends a paragraph ruling out.
 *
 * **A cycle is scoped to a version.** "Everyone acknowledged the expense policy"
 * has to stop being true after a rewrite, which means the version number is part
 * of the cycle's identity rather than a detail beside it, and a superseded cycle
 * has to read as history rather than as an outstanding obligation.
 *
 * The space name behind a `space_grant` audience comes from `GET /api/spaces`,
 * the list already cached for the pickers, rather than from `GET /api/spaces/:id`.
 * The audience space can be one the reader has no access to — that is the whole
 * point of asking "who holds a grant *there*" — and fetching it by id would
 * answer 403 and write a denial into §8.5's log for the sake of a label nobody
 * asked to see.
 */

import { useSpaces } from '../../api/queries.ts';
import type {
  AcknowledgmentAudience,
  AcknowledgmentCycle,
  DocumentStatus,
} from '../../api/types.ts';
import { formatDay } from '../../components/format.ts';
import { Badge, roleLabel } from '../../components/ui/badge.tsx';
import { Mono } from '../../components/ui/layout.tsx';
// Calendar-day arithmetic that happens to have been written beside the review
// date. It is DST-safe — both ends are local midnights, so it counts calendar
// days rather than 24-hour periods — and a second copy here would be a second
// place for that to be got wrong.
import { reviewDayDelta, reviewPhrase } from '../documents/review.tsx';

export function AudienceDescription({ audience }: { audience: AcknowledgmentAudience }) {
  const spaces = useSpaces();

  if (audience.kind === 'role') {
    return (
      <span>
        Everyone whose role is{' '}
        <span className="font-semibold text-primary">
          {audience.role ? roleLabel(audience.role) : 'unknown'}
        </span>
        , as it stands right now
      </span>
    );
  }

  const space = spaces.data?.spaces.find((row) => row.id === audience.spaceId);
  return (
    <span>
      Everyone who holds <span className="font-mono text-xs">{audience.capability}</span> on{' '}
      <span className="font-semibold text-primary">
        {space?.name ?? <Mono>{audience.spaceId}</Mono>}
      </span>
      , resolved through the same precedence every other access answer uses
    </span>
  );
}

/**
 * Why a cycle stopped being signable, or null while it still is.
 *
 * `current` is derived on the server — a cycle is live while its version is the
 * document's published one *and* the document has not been archived (§7.7) — so
 * one boolean covers two endings, and they are not interchangeable on screen:
 * "superseded" tells the reader a newer text is waiting, which is untrue of a
 * document that has been withdrawn altogether and would send them looking for a
 * revision nobody published. Every screen that renders a closed cycle asks this
 * one function rather than re-testing the status, so the distinction cannot be
 * drawn one way here and another way two files over.
 *
 * Structurally typed rather than taking a cycle, because the personal history
 * carries the same pair on a different shape.
 */
export type CycleClosure = 'superseded' | 'archived' | null;

export function cycleClosure(cycle: {
  current: boolean;
  documentStatus: DocumentStatus;
}): CycleClosure {
  if (cycle.current) return null;
  return cycle.documentStatus === 'archived' ? 'archived' : 'superseded';
}

/** Whether this cycle is the one that can still be signed, and if not, why not. */
export function CycleScopeBadge({ cycle }: { cycle: AcknowledgmentCycle }) {
  switch (cycleClosure(cycle)) {
    case null:
      return (
        <Badge tone="brand">
          <span title="This cycle is against the version readers currently resolve to.">
            Current · v{cycle.versionNumber}
          </span>
        </Badge>
      );
    case 'archived':
      return (
        <Badge tone="gray">
          <span title="The document has been archived, so this cycle is closed — acknowledgment is valid only on published documents (§7.7). Unarchiving reopens it.">
            Archived · v{cycle.versionNumber}
          </span>
        </Badge>
      );
    default:
      return (
        <Badge tone="gray">
          <span title="A newer version has been published, so this cycle is closed history — prior acknowledgments do not carry (§7.7).">
            Superseded · v{cycle.versionNumber}
          </span>
        </Badge>
      );
  }
}

/**
 * A due date, or the honest absence of one.
 *
 * "No date" is stated rather than left blank: a cycle with no due date is a
 * standing obligation nothing will ever chase, which is a real choice somebody
 * made and worth seeing.
 */
export function DueDate({ dueDate }: { dueDate: string | null }) {
  if (!dueDate) {
    return <span className="text-tertiary">No date — nothing will chase it</span>;
  }

  const delta = reviewDayDelta(dueDate);
  const overdue = delta !== null && delta < 0;

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className={overdue ? 'font-semibold text-warning-primary' : undefined}>
        {formatDay(dueDate)}
      </span>
      <Badge tone={overdue ? 'warning' : 'outline'}>Due {reviewPhrase(dueDate)}</Badge>
    </span>
  );
}
